import React, { useState, useRef } from "react";
import Papa from "papaparse";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Upload, Download, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

const REQUIRED_HEADERS = ["full_name", "phone"];
const OPTIONAL_HEADERS = ["email", "department", "source", "app_type_name", "notes"];

const TEMPLATE_CSV = `full_name,phone,email,department,source,app_type_name,notes
Aman Sharma,+91-99999-11111,aman@example.com,STUDY,Instagram,Study Permit,Interested in Sep intake
Priya Kaur,+91-99999-22222,priya@example.com,FILING,Facebook,Work Permit,Wants LMIA info`;

export default function LeadsImportDialog({ open, onOpenChange, meta, onImported }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [defaultDept, setDefaultDept] = useState("");
  const [defaultSource, setDefaultSource] = useState("Meta");
  const [defaultAppType, setDefaultAppType] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setRows([]); setFileName(""); setDefaultDept(""); setDefaultSource("Meta");
    setDefaultAppType(""); setResult(null); setImporting(false);
    if (inputRef.current) inputRef.current.value = "";
  };
  const close = () => { reset(); onOpenChange(false); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true, skipEmptyLines: true, transformHeader: h => h.trim().toLowerCase().replace(/\s+/g, "_"),
      complete: (res) => {
        const parsed = res.data.filter(r => r.full_name || r.phone || r.email);
        setRows(parsed);
        if (!parsed.length) toast.error("No usable rows found in CSV");
        else toast.success(`Parsed ${parsed.length} rows`);
      },
      error: (err) => toast.error("CSV parse failed: " + err.message),
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "leads-import-template.csv";
    a.click(); URL.revokeObjectURL(url);
  };

  const importNow = async () => {
    if (!rows.length) { toast.error("Upload a CSV first"); return; }
    if (!defaultDept && !rows.every(r => (r.department || "").trim())) {
      toast.error("Pick a default department, or make sure every row has a department column");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/leads/bulk-import", {
        rows, default_department: defaultDept || null,
        default_source: defaultSource, default_app_type_id: defaultAppType || null,
      });
      setResult(r.data);
      if (r.data.imported > 0) toast.success(`Imported ${r.data.imported} lead${r.data.imported === 1 ? "" : "s"}`);
      if (r.data.skipped_duplicates > 0) toast.message(`${r.data.skipped_duplicates} duplicate${r.data.skipped_duplicates === 1 ? "" : "s"} skipped (already in system by phone)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const finish = () => { onImported?.(); close(); };

  const allowedDepts = meta?.allowed_departments && meta.allowed_departments.length ? meta.allowed_departments : ["STUDY", "FILING"];
  const preview = rows.slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading-font">Import Leads from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV exported from Meta / Airtable / spreadsheet. Duplicates (same phone) are skipped automatically. Every row needs a department — set a batch default below if your CSV doesn't have one.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-3 p-3 border border-dashed border-slate-300 rounded-lg">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Upload className="w-5 h-5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" title={fileName}>{fileName || "Choose a .csv file"}</div>
                  <div className="text-xs text-slate-500">Required columns: <span className="mono">{REQUIRED_HEADERS.join(", ")}</span> · Optional: <span className="mono">{OPTIONAL_HEADERS.join(", ")}</span></div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input ref={inputRef} type="file" accept=".csv" onChange={onFile} className="hidden" id="leads-csv-input" data-testid="leads-csv-input" />
                <label htmlFor="leads-csv-input" className="inline-flex items-center px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm cursor-pointer hover:bg-slate-800">Choose CSV</label>
                <Button data-testid="leads-csv-template" variant="outline" size="sm" onClick={downloadTemplate}><Download className="w-4 h-4 mr-1" />Template</Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Default Department (fills empty rows)</Label>
                <Select value={defaultDept} onValueChange={setDefaultDept}>
                  <SelectTrigger data-testid="leads-import-default-dept"><SelectValue placeholder="Leave empty if CSV has it" /></SelectTrigger>
                  <SelectContent>{allowedDepts.map(d => <SelectItem key={d} value={d}>{d === "STUDY" ? "Study (Admissions)" : "Filing (Immigration)"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Default Source</Label>
                <Select value={defaultSource} onValueChange={setDefaultSource}>
                  <SelectTrigger data-testid="leads-import-default-source"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.sources || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Default File Type (if row doesn't match)</Label>
                <Select value={defaultAppType} onValueChange={setDefaultAppType}>
                  <SelectTrigger data-testid="leads-import-default-type"><SelectValue placeholder="Auto-match by name" /></SelectTrigger>
                  <SelectContent>{(meta?.app_types || []).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {rows.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center justify-between">
                  <span><strong>{rows.length}</strong> rows parsed · showing first {preview.length}</span>
                  <button onClick={() => { setRows([]); setFileName(""); if (inputRef.current) inputRef.current.value = ""; }} className="text-rose-600 hover:underline text-xs flex items-center"><X className="w-3 h-3 mr-0.5" />Clear</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-500 uppercase">
                      <tr>{["full_name", "phone", "email", "department", "source", "app_type_name", "notes"].map(h => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          {["full_name", "phone", "email", "department", "source", "app_type_name", "notes"].map(h => <td key={h} className="px-2 py-1">{r[h] || <span className="text-slate-300">—</span>}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <div className="font-semibold text-emerald-900">Imported {result.imported} lead{result.imported === 1 ? "" : "s"}</div>
                <div className="text-xs text-emerald-800">Skipped {result.skipped_duplicates} duplicate{result.skipped_duplicates === 1 ? "" : "s"} · {result.errors.length} error{result.errors.length === 1 ? "" : "s"}</div>
              </div>
            </div>
            {result.errors.length > 0 && (
              <div className="border border-amber-200 rounded-lg bg-amber-50 p-3">
                <div className="flex items-center gap-2 text-amber-900 font-medium text-sm mb-2"><AlertTriangle className="w-4 h-4" />Rows that could not be imported</div>
                <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                  {result.errors.map((e, i) => <div key={i}>Row {e.row}: <span className="text-amber-900">{e.reason}</span></div>)}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (<>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button data-testid="leads-import-confirm" onClick={importNow} disabled={!rows.length || importing} className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50">
              {importing ? "Importing…" : `Import ${rows.length || ""} leads`}
            </Button>
          </>) : (
            <Button data-testid="leads-import-done" onClick={finish} className="bg-slate-900 hover:bg-slate-800 text-white">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
